import { Box, Grid, SimpleGrid, Text } from '@chakra-ui/react';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { RThirdTitle } from '@resistapp/client/components/headers/r-third-title';
import { UrlLink } from '@resistapp/client/components/links/url-link';
import { GridCell } from '@resistapp/client/components/shared/grid';
import { GridView } from '@resistapp/client/components/shared/layout';
import { useMemo } from 'react';
import { useChipContext } from './use-chip-context';

export function ChipInfoAndLinksSection() {
  const {
    chipInfo,
    projects,
    flaggedAssays: highlightAssays,
    flaggedSamples: highlightSamples,
    sampleKeys,
    toggleGroupStable,
    toggleSampleStable,
    handleSelectBioSampleStable,
  } = useChipContext();

  const labSheetUrl = chipInfo?.labSheetUrl;
  const mmLot = chipInfo?.mmLot;
  const runDate = chipInfo?.runDate;
  const dispenser = chipInfo?.dispenser;
  const cycler = chipInfo?.cycler;

  // Group flagged technical replicates by their biological sample base id
  const groupedSamples = useMemo(() => {
    const getBase = (key: string) => key.replace(/-rep\d+$/, '');

    // Initialise mapping with all tech reps so that we know the full list for each base sample
    const mapping: Record<string, { allReps: string[]; flaggedReps: string[] }> = {};

    sampleKeys.forEach(k => {
      const base = getBase(k);
      mapping[base] = mapping[base] ?? { allReps: [], flaggedReps: [] };
      mapping[base].allReps.push(k);
    });

    highlightSamples.forEach(k => {
      const base = getBase(k);
      mapping[base] = mapping[base] ?? { allReps: [], flaggedReps: [] };
      mapping[base].flaggedReps.push(k);
    });

    // Keep only bases that have at least one flagged replicate
    return Object.entries(mapping)
      .filter(([, val]) => val.flaggedReps.length > 0)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [sampleKeys, highlightSamples]);

  return !chipInfo ? null : (
    <GridView style={{ paddingTop: '40px', paddingBottom: '40px' }}>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
        <Box>
          <RSecondTitle>Chip info</RSecondTitle>
          <Box mt={2}>
            {mmLot && (
              <Text fontSize="md" mb={1}>
                Mastermix lot: {mmLot}
              </Text>
            )}
            {runDate && (
              <Text fontSize="md" mb={1}>
                Run date: {new Date(runDate).toLocaleDateString()}
              </Text>
            )}
            {dispenser != null && (
              <Text fontSize="md" mb={1}>
                Dispenser: {dispenser}
              </Text>
            )}
            {cycler != null && (
              <Text fontSize="md" mb={1}>
                Cycler: {cycler}
              </Text>
            )}
          </Box>
        </Box>
        <Box>
          <RSecondTitle>Warnings</RSecondTitle>
          <Grid>
            {highlightAssays.length > 0 && (
              <GridCell widthpercentage={100}>
                <RThirdTitle>Assays w/o amplification</RThirdTitle>
                <Box as="ul" pl={4}>
                  {highlightAssays.map(a => (
                    <Box as="li" key={a}>
                      <Box
                        as="button"
                        onClick={() => {
                          toggleGroupStable(a, { ctrl: false, shift: false });
                        }}
                        style={{ textDecoration: 'underline', color: 'blue' }}
                      >
                        {a}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </GridCell>
            )}
            {groupedSamples.length > 0 && (
              <GridCell widthpercentage={100}>
                <RThirdTitle>Samples w/o amplification</RThirdTitle>
                <Box as="ul" pl={4}>
                  {groupedSamples.map(([base, { flaggedReps }]) => (
                    <Box as="li" key={base}>
                      <Box
                        as="button"
                        onClick={() => {
                          handleSelectBioSampleStable(base);
                        }}
                        style={{ textDecoration: 'underline', color: 'blue' }}
                      >
                        {base}
                      </Box>
                      <Box as="ul" pl={4}>
                        {flaggedReps.map(rep => (
                          <Box as="li" key={rep}>
                            <Box
                              as="button"
                              onClick={() => {
                                toggleSampleStable(rep, { ctrl: false, shift: false });
                              }}
                              style={{ textDecoration: 'underline', color: 'blue' }}
                            >
                              {rep}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </GridCell>
            )}
          </Grid>
        </Box>
        <Box>
          <RSecondTitle>Links</RSecondTitle>
          <Grid>
            {projects.length > 0 && (
              <GridCell widthpercentage={100}>
                <RThirdTitle>Projects</RThirdTitle>
                <Box display="flex" flexDirection="column" gap={1}>
                  {projects.map(p => (
                    <UrlLink key={p.id} text={p.name} href={`/research/${p.id}`} />
                  ))}
                </Box>
              </GridCell>
            )}
            {labSheetUrl && (
              <GridCell widthpercentage={100} style={{ marginTop: '10px' }}>
                <RThirdTitle>Lab sheet</RThirdTitle>
                <UrlLink text="Lab sheet" href={labSheetUrl} />
              </GridCell>
            )}
          </Grid>
        </Box>
      </SimpleGrid>
    </GridView>
  );
}
